import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, 
  Database, 
  TrendingUp, 
  Calculator,
  FileText,
  CheckCircle,
  Loader,
  DollarSign,
  BarChart3,
  Search
} from 'lucide-react';

const CBAGenerationAnimation = () => {
  const stages = [
    {
      id: 'analyzing',
      title: 'Analyzing Regulation',
      subtitle: 'Reading and parsing regulatory text...',
      icon: FileText,
      color: '#667eea',
      duration: 2000
    },
    {
      id: 'extracting',
      title: 'Extracting Requirements',
      subtitle: 'Identifying compliance obligations and impacts...',
      icon: Search,
      color: '#764ba2',
      duration: 2500
    },
    {
      id: 'calculating_costs',
      title: 'Calculating Costs',
      subtitle: 'Estimating implementation and operational expenses...',
      icon: Calculator,
      color: '#dc2626',
      duration: 3000
    },
    {
      id: 'calculating_benefits',
      title: 'Evaluating Benefits',
      subtitle: 'Quantifying economic and social improvements...',
      icon: TrendingUp,
      color: '#16a34a',
      duration: 3000
    },
    {
      id: 'sourcing',
      title: 'Verifying Sources',
      subtitle: 'Cross-referencing with regulatory databases...',
      icon: Database,
      color: '#2563eb',
      duration: 2000
    },
    {
      id: 'synthesizing',
      title: 'Synthesizing Analysis',
      subtitle: 'Computing net benefits and generating report...',
      icon: Brain,
      color: '#9333ea',
      duration: 2500
    },
    {
      id: 'complete',
      title: 'Analysis Complete',
      subtitle: 'Cost-benefit analysis ready for review',
      icon: CheckCircle,
      color: '#10b981',
      duration: 1000
    }
  ];

  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Auto-progress through stages
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          if (currentStage < stages.length - 1) {
            setCurrentStage(current => current + 1);
            return 0;
          }
          return 100;
        }
        return prev + 2;
      });
    }, stages[currentStage].duration / 50);

    return () => clearInterval(interval);
  }, [currentStage]);

  const current = stages[currentStage];
  const Icon = current.icon;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      padding: '40px',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)',
      borderRadius: '12px'
    }}>
      {/* Main Animation Container */}
      <div style={{ position: 'relative', marginBottom: '40px' }}>
        {/* Orbiting Elements */}
        <div style={{
          position: 'absolute',
          width: '200px',
          height: '200px',
          left: '-75px',
          top: '-75px'
        }}>
          {[0, 1, 2, 3].map((i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                width: '30px',
                height: '30px',
                background: current.color,
                borderRadius: '50%',
                opacity: 0.2
              }}
              animate={{
                x: [0, 100 * Math.cos(i * Math.PI / 2), 0],
                y: [0, 100 * Math.sin(i * Math.PI / 2), 0],
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.5, 0.2]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.75
              }}
            />
          ))}
        </div>

        {/* Central Icon */}
        <motion.div
          style={{
            width: '80px',
            height: '80px',
            background: 'white',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            zIndex: 10
          }}
          animate={{
            scale: [1, 1.05, 1],
            rotate: currentStage === stages.length - 1 ? 0 : [0, 5, -5, 0]
          }}
          transition={{
            duration: 2,
            repeat: currentStage === stages.length - 1 ? 0 : Infinity
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ duration: 0.3 }}
            >
              <Icon style={{ 
                width: '40px', 
                height: '40px', 
                color: current.color 
              }} />
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Pulse Effect */}
        {currentStage < stages.length - 1 && (
          <motion.div
            style={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              border: `2px solid ${current.color}`,
              borderRadius: '20px',
              left: 0,
              top: 0,
              zIndex: 5
            }}
            animate={{
              scale: [1, 1.3, 1.3],
              opacity: [0.5, 0, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity
            }}
          />
        )}
      </div>

      {/* Stage Info */}
      <motion.div
        key={current.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ textAlign: 'center', marginBottom: '24px' }}
      >
        <h3 style={{
          fontSize: '20px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '8px'
        }}>
          {current.title}
        </h3>
        <p style={{
          fontSize: '14px',
          color: '#6b7280'
        }}>
          {current.subtitle}
        </p>
      </motion.div>

      {/* Progress Bar */}
      <div style={{
        width: '300px',
        height: '8px',
        background: '#e5e7eb',
        borderRadius: '4px',
        overflow: 'hidden',
        marginBottom: '16px'
      }}>
        <motion.div
          style={{
            height: '100%',
            background: `linear-gradient(90deg, ${current.color} 0%, ${current.color}dd 100%)`,
            borderRadius: '4px'
          }}
          animate={{
            width: `${progress}%`
          }}
          transition={{
            duration: 0.1
          }}
        />
      </div>

      {/* Stage Indicators */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginTop: '24px'
      }}>
        {stages.slice(0, -1).map((stage, index) => (
          <div
            key={stage.id}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: index < currentStage ? stage.color :
                        index === currentStage ? stage.color :
                        '#e5e7eb',
              opacity: index <= currentStage ? 1 : 0.5,
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </div>

      {/* Data Stream Visualization */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        borderRadius: '12px'
      }}>
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              width: '2px',
              height: '50px',
              background: `linear-gradient(180deg, transparent, ${current.color}40, transparent)`,
              left: `${20 + i * 20}%`
            }}
            animate={{
              y: [-50, 450]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 0.6,
              ease: 'linear'
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default CBAGenerationAnimation;